import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDispatch } from "react-redux";
import styled from "styled-components";

import { showSuccessToast } from "app/actions/toast_actions";
import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import { swSettings } from "app/common/services/swSettings";
import { Spinner } from "pro-features/components/Loaders/src/Spinner";
import type { IProModalCustomStyles } from "pro-features/components/Modals/src/ProModal";
import { ProModal } from "pro-features/components/Modals/src/ProModal";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { i18nFilter } from "app/filters/i18nFilter";
import { ESelectionOptions, GroupShareModal } from "app/pages/workspace/marketing/pages/GroupShareModal";
import type { IAccountUser } from "app/sharing/SharingService";
import { SharingService } from "app/sharing/SharingService";
import { allTrackers } from "app/services/track/track";

const getProModalStyles: (isConfirmation) => IProModalCustomStyles = (isConfirmation) => {
    return {
        content: {
            width: "434px",
            boxSizing: "content-box",
            minHeight: isConfirmation ? "auto" : "263px",
            display: "flex",
            alignItems: "center",
        },
    };
};

const LoadingSpinner = styled(Spinner)`
    margin: 0 auto;
    width: 55px;
    height: 55px;
`;

const getInitialSelectedOption = (group) => {
    const { sharedWithAccounts, sharedWithUsers } = group ?? { sharedWithAccounts: [], sharedWithUsers: [] };
    if (sharedWithAccounts.length > 0) {
        return ESelectionOptions.SHARED_WITH_ACCOUNT;
    } else if (sharedWithUsers.length > 0) {
        return ESelectionOptions.SHARED_WITH_USERS;
    } else {
        return ESelectionOptions.NOT_SHARED;
    }
};

const sortUsers = (users) => {
    return users.sort((a, b) => (a.LastName > b.LastName ? 1 : -1));
};

interface IKeywordGroupsShareModalProps {
    keywordGroup: any;
    onCloseClick?: VoidFunction;
    onFinish?: (group) => void;
    isOpen?: boolean;
    users?: IAccountUser[];
}

export const KeywordGroupsShareModal: React.FC<IKeywordGroupsShareModalProps> = (props) => {
    const initialSelectedOption = useRef<ESelectionOptions>(getInitialSelectedOption(props.keywordGroup));
    const [selectedOption, setSelectedOption] = useState<ESelectionOptions>(initialSelectedOption.current);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [notifyUsers, setNotifyUsers] = useState(false);
    const [message, setMessage] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [confirmation, setConfirmation] = useState(false);
    const [error, setError] = useState(false);
    // set the users as users from prop, or empty array and load them inside the useeffect
    const [allUsers, setAllUsers] = useState(props.users?.length > 0 ? sortUsers(props.users) : []);
    const dispatch = useDispatch();
    const translate = useTranslation();

    useEffect(() => {
        allTrackers.dispatchTrackEvent("Pop Up", "open", "Share keyword group");
        const applySelectedUsers = (users) => {
            let selected = [];
            if (props.keywordGroup.sharedWithUsers.length > 0) {
                selected = users.filter(({ Id }) => {
                    return props.keywordGroup.sharedWithUsers.includes(Id.toString());
                });
            }
            setSelectedUsers(selected);
        };
        const getAccountUsers = async () => {
            setLoadingUsers(true);
            const { users } = await SharingService.getAccountUsers();
            setAllUsers(sortUsers(users));
            applySelectedUsers(users);
            setLoadingUsers(false);
        };
        if (allUsers.length == 0) {
            getAccountUsers();
        } else {
            applySelectedUsers(allUsers);
        }
        return () => {
            allTrackers.dispatchTrackEvent("Pop Up", "close", "Share keywrod group");
        };
    }, []);
    const onOptionClick = (option: ESelectionOptions) => {
        setSelectedOption(option);
    };
    const onUserSelect = useCallback(
        (user) => {
            const newSelectedUsers = [...selectedUsers];
            const userIndex = selectedUsers.findIndex(({ Id }) => Id === user.Id);
            if (userIndex > -1) {
                newSelectedUsers.splice(userIndex, 1);
            } else {
                newSelectedUsers.push(user);
            }
            setSelectedUsers(newSelectedUsers);
        },
        [selectedUsers],
    );

    const onNotifyUsersToggle = () => {
        setNotifyUsers(!notifyUsers);
    };

    const onMessageChange = (updatedMessage: string) => {
        setMessage(updatedMessage);
    };
    const ShowSucceedToast = () => {
        dispatch(
            showSuccessToast(
                getToastItemComponent({
                    text: translate("keyword.groups.sharing.success"),
                }),
            ),
        );
    };
    const onApprove = async () => {
        setLoading(true);
        const group = await keywordGroupsQueryClient.shareGroup(props.keywordGroup.id, {
            accountIds: [swSettings.user.accountId as string],
            message: message?.toString(),
            userids: [],
            newUsersAdd: [],
        });
        setLoading(false);
        setConfirmation(false);
        setMessage("");
        allTrackers.dispatchTrackEvent("Pop Up", "submit-ok", "Share keyword group/with-account/");
        props.onFinish?.(group);
        ShowSucceedToast();
    };

    const onSubmit = async () => {
        try {
            setError(false);
            const selected = allUsers.filter(({ Id }) => {
                return props.keywordGroup.sharedWithUsers.includes(Id.toString());
            });
            const addUsers = selectedUsers?.filter((user) => {
                return !selected.some((item) => item === user);
            });
            let group;
            switch (selectedOption) {
                case ESelectionOptions.NOT_SHARED:
                    // no need to unshare when the group is already unshared
                    if (initialSelectedOption.current !== ESelectionOptions.NOT_SHARED) {
                        setLoading(true);
                        group = await keywordGroupsQueryClient.removeGroupSharing(props.keywordGroup.id);
                        setLoading(false);
                        setMessage("");
                        allTrackers.dispatchTrackEvent("Pop Up", "submit-ok", "Share keyword group/remove-sharing/");
                        props.onFinish?.(group);
                        dispatch(
                            showSuccessToast(
                                getToastItemComponent({
                                    text: translate("keyword.groups.unshare.success"),
                                }),
                            ),
                        );
                    } else {
                        onCancel();
                    }
                    return;
                case ESelectionOptions.SHARED_WITH_ACCOUNT:
                    // when sharing with an entire account, a confirmation modal will be displayed
                    notifyUsers ? setConfirmation(true) : onApprove();
                    return;
                case ESelectionOptions.SHARED_WITH_USERS:
                    setLoading(true);
                    group = await keywordGroupsQueryClient.shareGroup(props.keywordGroup.id, {
                        accountIds: [],
                        userids: selectedUsers.map((user) => user.Id.toString()),
                        newUsersAdd: addUsers.map((user) => user.Id.toString()),
                        message: message?.toString(),
                    });
                    setLoading(false);
                    setMessage("");
                    allTrackers.dispatchTrackEvent(
                        "Pop Up",
                        "submit-ok",
                        `Share keyword group/with-users/${selectedUsers.length || ""}`,
                    );
                    props.onFinish?.(group);
                    ShowSucceedToast();
            }
        } catch (e) {
            setLoading(false);
            setError(true);
        }
    };

    const onCancel = useCallback(() => {
        if (confirmation) {
            setConfirmation(false);
        } else {
            props.onCloseClick?.();
        }
    }, [confirmation]);

    const proModalStyles = useMemo(() => {
        return getProModalStyles(confirmation);
    }, [confirmation]);
    const submitButtonText = useMemo(() => {
        if (selectedOption !== initialSelectedOption.current) {
            switch (selectedOption) {
                case ESelectionOptions.NOT_SHARED:
                    return i18nFilter()("keyword.groups.sharing.removeaccess");
                case ESelectionOptions.SHARED_WITH_ACCOUNT:
                case ESelectionOptions.SHARED_WITH_USERS:
                    return i18nFilter()("keyword.groups.sharing.share");
            }
        } else {
            return i18nFilter()("keyword.groups.sharing.done");
        }
    }, [selectedOption]);

    return (
        <ProModal
            isOpen={props.isOpen}
            onCloseClick={props.onCloseClick}
            shouldCloseOnOverlayClick={true}
            customStyles={proModalStyles}
        >
            {loadingUsers ? (
                <LoadingSpinner />
            ) : (
                <GroupShareModal
                    onApprove={onApprove}
                    onRemoveUser={onUserSelect}
                    selectedUsers={selectedUsers}
                    allUsers={allUsers}
                    onOptionClick={onOptionClick}
                    submitButtonText={submitButtonText}
                    submitButtonDisabled={
                        selectedOption === ESelectionOptions.SHARED_WITH_USERS ? selectedUsers.length === 0 : false
                    }
                    selectedOption={selectedOption}
                    onUserSelect={onUserSelect}
                    onNotifyUsersToggle={onNotifyUsersToggle}
                    onSubmit={onSubmit}
                    onCancel={onCancel}
                    onMessageChange={onMessageChange}
                    isLoading={loading}
                    confirmation={confirmation}
                    error={error}
                    notifyUsers={notifyUsers}
                    showCancel={true}
                    groupName={props.keywordGroup?.name}
                />
            )}
        </ProModal>
    );
};
